import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import i18n from 'i18n-react';

import {
  clearPersistedThread,
  mergeConversationIdForFirstMessage,
  readPersistedThread,
  saveConversation,
  savePersistedThread,
} from './smartAssistStorage';
import type {
  ConversationEntry,
  Message,
  MessageQuota,
  PersistedThread,
  RagLink,
  RecentPanelRect,
  TrainingProgress,
} from './types';
import { useConversationHistory } from './useConversationHistory';
import { useConversationList } from './useConversationList';
import { useRecentPanel } from './useRecentPanel';
import { type ConversationIdMergeHandler, useSmartAssistStream } from './useSmartAssistStream';
import { useSuggestions } from './useSuggestions';

export interface UseSmartAssistProps {
  firstName?: string;
  destination?: string;
  appId?: number | string | null;
  baseUrl: string;
  getToken: () => Promise<string | null>;
}

export interface UseSmartAssistReturn {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  focused: boolean;
  setFocused: React.Dispatch<React.SetStateAction<boolean>>;
  chatOpen: boolean;
  messages: Message[];
  historyLoading: boolean;
  messageQuota: MessageQuota | null;
  trainingProgress: TrainingProgress | null;
  recentPanelRect: RecentPanelRect | null;
  deletingConvId: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  chatInputRef: React.RefObject<HTMLInputElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  recentPanelRef: React.RefObject<HTMLDivElement | null>;
  lastMessageRowRef: React.RefObject<HTMLDivElement | null>;
  greeting: string;
  destinationLabel: string;
  sidebarRows: ConversationEntry[];
  recentForPortal: ConversationEntry[];
  ragBarItems: RagLink[];
  isStreaming: boolean;
  isQuotaBlocked: boolean;
  persistedThread: PersistedThread | null;
  activeSidebarThreadId: string | null;
  quickSuggestionsHome: string[];
  quickSuggestionsChat: string[];
  showHomeSuggestionSwitch: boolean;
  showChatSuggestionSwitch: boolean;
  openChat(initialQuery?: string): void;
  openSavedConversation(entry: ConversationEntry): void;
  openPastConversations: () => void;
  closeChat: () => void;
  handleNewChat: () => void;
  handleSubmit(event: React.FormEvent): void;
  handleChatSubmit(event: React.FormEvent): void;
  handleSuggestionClick(suggestion: string): void;
  handleHeadingClick: () => void;
  handleHeadingKeyDown(event: React.KeyboardEvent): void;
  rotateHomeSuggestions: () => void;
  rotateChatSuggestions: () => void;
  handleDeleteConversation(conversationId: string): Promise<void>;
}

export function useSmartAssist({
  firstName,
  destination,
  appId,
  baseUrl,
  getToken,
}: UseSmartAssistProps): UseSmartAssistReturn {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // ─── Shared refs (passed down to sub-hooks) ───────────────────────────────
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const recentPanelRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRowRef = useRef<HTMLDivElement | null>(null);
  const msgIdRef = useRef(0);
  const conversationIdRef = useRef<string | null>(null);
  const refreshServerConversationsRef = useRef<(() => Promise<void>) | null>(null);
  const onMergeConversationIdRef = useRef<ConversationIdMergeHandler | null>(null);

  const nextId = (): number => {
    msgIdRef.current += 1;
    return msgIdRef.current;
  };

  const destinationLabel = destination ?? (i18n.translate('smartAssist.defaultDestination') as string);
  const greeting = firstName
    ? (i18n.translate('smartAssist.greetingName', { firstName }) as string)
    : (i18n.translate('smartAssist.greetingFallback') as string);

  const resolveEffectiveAppId = useCallback((): number | null => {
    const n = Number(appId);
    if (n > 0) return n;
    const fromEnv = Number(import.meta.env.VITE_APP_ID);
    if (fromEnv > 0) return fromEnv;
    return null;
  }, [appId]);

  // ─── Sub-hooks ────────────────────────────────────────────────────────────

  const {
    messages,
    setMessages,
    messageQuota,
    trainingProgress,
    streamAbortRef,
    isStreaming,
    isQuotaBlocked,
    sendAgentMessage,
  } = useSmartAssistStream({
    resolveEffectiveAppId,
    baseUrl,
    getToken,
    conversationIdRef,
    msgIdRef,
    refreshServerConversationsRef,
    onMergeConversationIdRef,
  });

  const { setConversations, sidebarRows, recentForPortal, deletingConvId, handleDeleteConversation } =
    useConversationList({
      appId,
      baseUrl,
      getToken,
      chatOpen,
      conversationIdRef,
      setMessages,
      resolveEffectiveAppId,
      refreshServerConversationsRef,
    });

  // Wire merge callback now that setConversations is available
  useEffect(() => {
    onMergeConversationIdRef.current = {
      merge(messageText, conversationId) {
        const merged = mergeConversationIdForFirstMessage(messageText, conversationId);
        if (merged) setConversations(merged);
      },
    };
  }, [setConversations]);

  const {
    quickSuggestionsHome,
    quickSuggestionsChat,
    showHomeSuggestionSwitch,
    showChatSuggestionSwitch,
    rotateHomeSuggestions,
    rotateChatSuggestions,
  } = useSuggestions({ appId, baseUrl, getToken, messages });

  const { historyLoading, triggerHistoryLoad } = useConversationHistory({
    chatOpen,
    messages,
    appId,
    baseUrl,
    getToken,
    conversationIdRef,
    msgIdRef,
    setMessages,
    resolveEffectiveAppId,
  });

  const { recentPanelRect } = useRecentPanel({
    focused,
    recentForPortalCount: recentForPortal.length,
    sidebarRowsCount: sidebarRows.length,
    wrapperRef,
  });

  // ─── Scroll to last message ───────────────────────────────────────────────

  const lastMessage = messages.length ? messages[messages.length - 1] : undefined;
  const lastMessageAnchorId = lastMessage ? lastMessage.id : null;
  useLayoutEffect(() => {
    if (lastMessageAnchorId === null) return;
    lastMessageRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }, [lastMessageAnchorId]);

  // ─── Click-outside to close focus ────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const inWidget = wrapperRef.current?.contains(e.target as Node);
      const inRecent = recentPanelRef.current?.contains(e.target as Node);
      if (!inWidget && !inRecent) setFocused(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ─── Focus chat input when overlay opens ─────────────────────────────────

  useEffect(() => {
    if (chatOpen && chatInputRef.current) chatInputRef.current.focus();
  }, [chatOpen]);

  // ─── Chat open / close handlers ───────────────────────────────────────────

  const openChat = useCallback(
    (initialQuery?: string): void => {
      const q = initialQuery ?? query;
      if (!q.trim()) return;
      if (messageQuota?.allowed === false) {
        setChatOpen(true);
        setFocused(false);
        setQuery('');
        return;
      }
      const aid = resolveEffectiveAppId();
      if (aid) clearPersistedThread(String(aid));
      conversationIdRef.current = null;
      setConversations(saveConversation(q));
      setMessages([{ id: nextId(), role: 'user', text: q, streaming: false, links: [] }]);
      setChatOpen(true);
      setFocused(false);
      setQuery('');
      void sendAgentMessage(q, { resetConversation: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, sendAgentMessage, resolveEffectiveAppId, messageQuota],
  );

  const openSavedConversation = useCallback(
    (entry: ConversationEntry): void => {
      if (!entry?.text?.trim()) return;
      if (entry.conversationId) {
        const aid = resolveEffectiveAppId();
        if (!aid) return;
        conversationIdRef.current = entry.conversationId;
        savePersistedThread(String(aid), entry.conversationId);
        setFocused(false);
        setQuery('');
        setMessages([]);
        setChatOpen(true);
        triggerHistoryLoad();
        return;
      }
      openChat(entry.text);
    },
    [resolveEffectiveAppId, openChat, triggerHistoryLoad, setMessages],
  );

  const closeChat = (): void => {
    streamAbortRef.current?.abort();
    conversationIdRef.current = null;
    setChatOpen(false);
    setMessages([]);
    setQuery('');
  };

  const handleNewChat = (): void => {
    streamAbortRef.current?.abort();
    conversationIdRef.current = null;
    const aid = resolveEffectiveAppId();
    if (aid) clearPersistedThread(String(aid));
    setMessages([]);
    setQuery('');
  };

  const openPastConversations = (): void => {
    void refreshServerConversationsRef.current?.();
    const aid = resolveEffectiveAppId();
    const persisted = aid ? readPersistedThread(String(aid)) : null;
    conversationIdRef.current = persisted?.conversationId ?? null;
    setFocused(false);
    setQuery('');
    setMessages([]);
    setChatOpen(true);
    if (persisted?.conversationId) triggerHistoryLoad();
  };

  // ─── Form submit handlers ─────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    openChat();
  };

  const handleChatSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!query.trim() || messageQuota?.allowed === false) return;
    const userMsg: Message = { id: nextId(), role: 'user', text: query, streaming: false, links: [] };
    setMessages((prev) => {
      return [...prev, userMsg];
    });
    setQuery('');
    void sendAgentMessage(userMsg.text, { resetConversation: false });
  };

  const handleSuggestionClick = (suggestion: string): void => {
    if (messageQuota?.allowed === false) return;
    if (chatOpen) {
      setMessages((prev) => {
        return [...prev, { id: nextId(), role: 'user', text: suggestion, streaming: false, links: [] }];
      });
      void sendAgentMessage(suggestion, { resetConversation: false });
    } else {
      openChat(suggestion);
    }
  };

  // ─── Heading interaction ──────────────────────────────────────────────────

  const handleHeadingClick = useCallback((): void => {
    const aid = resolveEffectiveAppId();
    if (!aid) {
      inputRef.current?.focus();
      return;
    }
    const persisted = readPersistedThread(String(aid));
    if (persisted?.conversationId) {
      conversationIdRef.current = persisted.conversationId;
      setFocused(false);
      setQuery('');
      setMessages([]);
      setChatOpen(true);
      triggerHistoryLoad();
      return;
    }
    inputRef.current?.focus();
  }, [resolveEffectiveAppId, triggerHistoryLoad, setMessages]);

  const handleHeadingKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleHeadingClick();
      }
    },
    [handleHeadingClick],
  );

  // ─── Derived values ───────────────────────────────────────────────────────

  const lastAssistantLinks = [...messages].reverse().find((m) => {
    return m.role === 'assistant' && m.links?.length;
  })?.links;
  const ragBarItems = (lastAssistantLinks ?? [])
    .filter((l) => {
      return l.cms?.url;
    })
    .slice(0, 8);
  const aidForThread = resolveEffectiveAppId();
  const persistedThread = aidForThread ? readPersistedThread(String(aidForThread)) : null;
  const activeSidebarThreadId = chatOpen ? (persistedThread?.conversationId ?? null) : null;

  return {
    query,
    setQuery,
    focused,
    setFocused,
    chatOpen,
    messages,
    historyLoading,
    messageQuota,
    trainingProgress,
    recentPanelRect,
    deletingConvId,
    inputRef,
    chatInputRef,
    wrapperRef,
    recentPanelRef,
    lastMessageRowRef,
    greeting,
    destinationLabel,
    sidebarRows,
    recentForPortal,
    ragBarItems,
    isStreaming,
    isQuotaBlocked,
    persistedThread,
    activeSidebarThreadId,
    quickSuggestionsHome,
    quickSuggestionsChat,
    showHomeSuggestionSwitch,
    showChatSuggestionSwitch,
    openChat,
    openSavedConversation,
    openPastConversations,
    closeChat,
    handleNewChat,
    handleSubmit,
    handleChatSubmit,
    handleSuggestionClick,
    handleHeadingClick,
    handleHeadingKeyDown,
    rotateHomeSuggestions,
    rotateChatSuggestions,
    handleDeleteConversation,
  };
}
